import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, XCircle } from 'lucide-react';
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import {
    YELLOW,
    noImage,
    isDeviceDefaultUrl,
    filenameFromUrl,
    sameImageUrl,
    tabForSource,
    SOURCE_ITEM,
    UPLOAD_ACCEPTED_TYPES,
    UPLOAD_MAX_SIZE,
    UPLOAD_ERROR_MESSAGE,
    UPLOAD_DUPLICATE_MESSAGE,
    fetchTakenFilenames,
} from './tradeInImages.js';

// ─── Hover label for an Upload-tab tile (ERP-5022) ────────────────────────────
// Shows the filename on a successful upload and the failure reason on a rejected one,
// trailing the cursor while it moves over the tile.
//
// Not the shared addentities/Tooltip: that one is a fixed 309px panel with an arrow,
// anchored centred above the element — so in this wrapping grid it sat on top of the tile
// (and its neighbours) instead of following the pointer, and it's several times wider than
// the compact pill the spec shows. It's used on other pages, so it's left alone.
//
// cloneElement instead of a wrapper element: the tiles are flex children of the grid, and a
// wrapping span would become the flex item in place of the tile itself.
function UploadTileTooltip({ label, children }) {
    // Anchored to the hovered tile (centered above it, caret pointing down) to match the
    // Stock List "Pending" info tooltip style rather than following the cursor.
    const [tip, setTip] = useState(null); // { top, left }
    if (!label) return children;

    const show = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setTip({ top: r.top, left: r.left + r.width / 2 });
    };
    const hide = () => setTip(null);

    return (
        <>
            {React.cloneElement(children, {
                onMouseEnter: show,
                onMouseLeave: hide,
            })}
            {tip && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: tip.top,
                        left: tip.left,
                        transform: 'translate(-50%, calc(-100% - 10px))',
                        padding: '10px 12px',
                        borderRadius: 8,
                        background: '#495057',
                        color: '#FFFFFF',
                        fontFamily: 'Montserrat',
                        fontSize: 12,
                        fontWeight: 400,
                        lineHeight: 1.5,
                        maxWidth: 240,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        textAlign: 'center',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.18)',
                        // Never let the bubble intercept the hover that produced it, or sit under
                        // the cursor and flicker between enter/leave.
                        pointerEvents: 'none',
                        zIndex: 10000,
                    }}
                >
                    {label}
                    {/* Down-caret, centered on the tile below (matches Stock List Pending tooltip) */}
                    <div
                        style={{
                            position: 'absolute',
                            left: '50%',
                            top: '100%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '8px solid transparent',
                            borderRight: '8px solid transparent',
                            borderTop: '8px solid #495057',
                        }}
                    />
                </div>,
                document.body,
            )}
        </>
    );
}

// ─── Remove-image badge (always visible, top-right; click removes immediately) ─
function RemoveImageControl({ onRemove }) {
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            style={{
                position: 'absolute', top: 4, right: 4,
                width: 22, height: 22, borderRadius: '50%',
                background: '#E9ECEF', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 2,
            }}
        >
            <X size={12} color="#212529" />
        </button>
    );
}

// A saved tile lives in one of two tables (see tradeInImages.js) and its selection has to
// name which. Anything already on a pool tab counts as "came from an upload".
const isPoolTab = (tab) => tab === 'tradein' || tab === 'listing';

// ─── Trade-In Image Picker ────────────────────────────────────────────────────
// The Upload/SPU picker shared by Step 1's Listing Image and Step 2's Listing Items
// (ERP-5022). Both levels draw from one listing-wide upload pool, so the same file can be
// picked from either — which is why every saved tile carries the `source` that says which
// table its id belongs to, and why removals are reported as {id, source} rather than bare ids.
//
// `ownSource` is the level rendering this picker. Uploads made here become that level's rows
// on save; pool entries from the other level are selectable but stay owned by whoever
// uploaded them.
export default function TradeInImagePickerModal({
    imageLabel = 'Listing Item Image',
    ownSource = SOURCE_ITEM,
    images,
    initialSelected: rawInitialSelected,
    initialUploadedImages,
    sessionUploads,
    onAddSessionUpload,
    onRemoveSessionUpload,
    uploadCounterRef,
    deviceDefaultImage,
    excludeListingId,
    onCancel,
    onConfirm,
}) {

    // A prior save can persist a snapshot of whatever the device default was AT THE TIME
    // (e.g. Phone, before the Listing Device was later changed to Watch) as if it were a real
    // chosen image — the backend has no concept of "this is just the placeholder". Recognize
    // it by URL so it's treated as a placeholder here too, not as a distinct real image.
    const initialSelected = (rawInitialSelected && !rawInitialSelected.isDeviceDefault && isDeviceDefaultUrl(rawInitialSelected.url))
        ? { ...rawInitialSelected, isDeviceDefault: true, isPlaceholder: true }
        : rawInitialSelected;

    const matchesSpuImage = Boolean(initialSelected?.url && images.some(s =>
        (initialSelected.id && s.id === initialSelected.id) || sameImageUrl(s.url, initialSelected.url)
    ));
    const hasUploadHistory = (initialUploadedImages || []).length > 0;

    // The backend doesn't persist which tab (SPU vs. upload history) a saved image originally
    // came from — only *this session's* fresh pick carries that (`isUploaded` or a pool tab);
    // on a fresh reload neither is set. If the saved image doesn't match anything in the SPU's
    // live image list AND there is upload history, it almost certainly came from an upload —
    // treat it as such instead of misplacing it under the SPU tab.
    const initialIsUpload = Boolean(
        initialSelected?.isUploaded ||
        initialSelected?.isDeviceDefault ||
        isPoolTab(initialSelected?.tab) ||
        (initialSelected?.url && !matchesSpuImage && hasUploadHistory)
    );
    const [activeTab, setActiveTab] = useState(initialIsUpload ? 'upload' : 'spu');
    const fileInputRef = useRef(null);
    // Ids this level owns: its own restored history (seeded up front) plus anything it uploads
    // fresh in this session — as opposed to pool entries merely shown here because the other
    // level uploaded/saved them (ERP-5022 shared pool). On confirm, only these (plus whichever
    // pool entry the user actively selects) get persisted here — otherwise every pooled upload
    // would get attached to this level too.
    const ownIdsRef = useRef(new Set((initialUploadedImages || []).map(u => u.id)));

    // The backend only stores a generated snapshot of the chosen image, not which source
    // SPU image it came from — so on reopening after a save, the previously-selected SPU-tab
    // image usually won't match anything in `images` even though the SPU's images haven't
    // changed. Rather than fall back to "select the first image", pin the actual
    // previously-saved image as its own tile so it still shows selected.
    const spuCurrentTile = (!initialIsUpload && initialSelected?.url && !matchesSpuImage)
        ? { ...initialSelected, id: '__current__', tab: 'spu', isCurrentPin: true }
        : null;
    const displaySpuImages = spuCurrentTile ? [spuCurrentTile, ...images] : images;

    // The backend re-snapshots the active image on every save, so its URL never matches any
    // `tradein_images[].url` (confirmed against the actual API response) — but where the caller
    // could resolve the real history-row id (via the original filename, see the step loaders),
    // `initialSelected.id` will already point at it directly. Match by id first; URL is only a
    // fallback for whatever wasn't resolvable upstream.
    const uploadHistoryCandidates = (initialUploadedImages || []).filter(u => !isDeviceDefaultUrl(u.preview));
    const matchedUploadEntry = (initialIsUpload && initialSelected)
        ? uploadHistoryCandidates.find(u =>
            (initialSelected.id && u.id === initialSelected.id) ||
            (initialSelected.url && sameImageUrl(u.preview, initialSelected.url))
          ) || null
        : null;

    // Only genuinely unmatched cases (no upload history entry shares this id/URL at all) fall back
    // to a pinned "current" tile — we can't recover which upload-history entry it was, only that
    // it belongs in this tab.
    const uploadCurrentTile = (initialIsUpload && initialSelected?.url && !initialSelected?.isUploaded && !initialSelected?.isDeviceDefault && !isPoolTab(initialSelected?.tab) && !matchedUploadEntry)
        ? { id: '__current__', preview: initialSelected.url, name: initialSelected.name || filenameFromUrl(initialSelected.url) || 'Current image', error: null, isSaved: true, isCurrentPin: true, source: ownSource }
        : null;

    // A saved tile's selection carries the source that decides which table the backend
    // resolves its id against. Getting this wrong saves a different picture entirely.
    const selectionFromSaved = (tile) => ({
        id: tile.id,
        url: tile.preview,
        source: tile.source || ownSource,
        tab: tabForSource(tile.source || ownSource),
        isCurrentPin: tile.isCurrentPin,
    });

    // Determine which image to show when modal opens
    const resolveInitial = () => {
        if (spuCurrentTile) return spuCurrentTile;
        if (matchedUploadEntry) return selectionFromSaved(matchedUploadEntry);
        if (uploadCurrentTile) return selectionFromSaved(uploadCurrentTile);
        if (initialIsUpload) return initialSelected;
        if (!initialSelected) {
            const first = images[0] || null;
            return first ? { ...first, tab: 'spu' } : null;
        }

        const found = images.find(s =>
            (initialSelected.id && s.id === initialSelected.id) ||
            sameImageUrl(s.url, initialSelected.url)
        );
        if (found) return { ...found, tab: 'spu' };

        // Previously selected image has been deleted → fallback to first image
        return images[0] ? { ...images[0], tab: 'spu' } : null;
    };

    const [selected, setSelected] = useState(resolveInitial());
    // Every file ever uploaded at this level (not just the selected one) persists across
    // reopening the modal — all of them get saved, not just the pick. The pinned "current"
    // tile (see uploadCurrentTile above) is included here too so it actually renders as a
    // highlighted option, not just as the `selected` state with nothing to point at.
    const [uploadedImages, setUploadedImages] = useState(() => {
        // A device-default snapshot must never show up as real upload history (see
        // isDeviceDefaultUrl) — drop it here even if it slipped into persisted data.
        const restored = (initialUploadedImages || [])
            .filter(u => !isDeviceDefaultUrl(u.preview))
            .map(u => ({ ...u, source: u.source || ownSource, error: null }));
        // Listing-wide shared pool (ERP-5022): every image uploaded anywhere on this listing —
        // by the listing itself or any of its items, in an earlier session or this one — is
        // shown here too. Dedupe against this level's own history so a file it already selected
        // (which lives in both places) isn't rendered twice.
        const restoredIds = new Set(restored.map(u => u.id));
        const shared = (sessionUploads || [])
            .filter(u => !restoredIds.has(u.id) && !isDeviceDefaultUrl(u.preview))
            .map(u => ({ ...u, source: u.source || ownSource, error: null }));
        // One tile per photo. Keyed on the original filename rather than the URL: every row that
        // uses a file keeps its own independent copy of it, so the same photo legitimately arrives
        // under different ids AND different URLs, and URL-matching would show it once per row.
        // Filenames are unique within a listing (duplicates are rejected at upload), so they're
        // the reliable identity; `preview` is only a fallback for an entry with no name.
        const seen = new Set();
        const base = [...restored, ...shared].filter(u => {
            const key = u.name || u.preview;
            if (!key) return true;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
        return uploadCurrentTile ? [uploadCurrentTile, ...base] : base;
    });
    // Already-persisted upload rows removed during this session, as {id, source}. They have to
    // be named explicitly on save: the payload otherwise only ever carries brand-new files, so
    // the backend has no way to tell a removal from a no-op. The source rides along because
    // only the level that owns a row can delete it through its own payload — a removal aimed at
    // the other level is carried through the wizard until it reaches the step that owns it.
    const [removedSaved, setRemovedSaved] = useState([]);

    const isSameImage = (a, b) => {
        if (!a || !b) return false;
        if (a.id !== null && b.id !== null) return a.id === b.id;
        return sameImageUrl(a.url, b.url);
    };

    // Clicking an image always selects it — it only changes when a *different*
    // image is picked, clicking the already-selected one again does nothing.
    const toggle = (img) => {
        setSelected({ ...img, tab: 'spu' });
    };

    const isSelected = (img) => isSameImage(selected, img);

    const toggleUploaded = (img) => {
        if (img.error) return;
        setSelected(prev => {
            if (prev?.id === img.id) return prev;
            // Already-persisted upload (from a previous save) — resolve it like any other
            // real backend image reference, no re-encoding needed.
            if (img.isSaved) return selectionFromSaved(img);
            return { id: img.id, url: img.preview, file: img.file, name: img.name, isUploaded: true, source: ownSource };
        });
    };

    const handleFilesSelected = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        // Filenames already present (valid or errored) block re-uploading the same name.
        // Seeded from the tiles on screen — which include the whole shared pool, so a name
        // another level already uploaded is caught too — then grown as we walk the batch so a
        // duplicate *within* the same selection is caught as well.
        const existingNames = new Set(uploadedImages.map(i => i.name));
        // Whatever clears the local checks still has to clear the other listings. Resolved
        // up front rather than after the tiles render, so a file is never shown as accepted
        // and then retracted — and so it can't reach the shared session pool in between.
        const candidates = [...new Set(
            files
                .filter(f => UPLOAD_ACCEPTED_TYPES.includes(f.type) && f.size <= UPLOAD_MAX_SIZE)
                .map(f => f.name)
                .filter(name => !existingNames.has(name))
        )];
        const takenElsewhere = await fetchTakenFilenames(candidates, excludeListingId);
        const newEntries = files.map(file => {
            uploadCounterRef.current += 1;
            const id = `upload-${uploadCounterRef.current}`;
            const isValidType = UPLOAD_ACCEPTED_TYPES.includes(file.type) && file.size <= UPLOAD_MAX_SIZE;
            const isDuplicate = existingNames.has(file.name) || takenElsewhere.has(file.name);
            existingNames.add(file.name);
            const error = !isValidType ? UPLOAD_ERROR_MESSAGE : (isDuplicate ? UPLOAD_DUPLICATE_MESSAGE : null);
            return error
                ? { id, file, preview: null, name: file.name, error, source: ownSource }
                : { id, file, preview: URL.createObjectURL(file), name: file.name, error: null, source: ownSource };
        });
        setUploadedImages(prev => [...prev, ...newEntries]);
        newEntries.forEach(e => ownIdsRef.current.add(e.id));
        // Add valid uploads to the listing-wide shared pool so the other level's Upload tab
        // sees them too (ERP-5022). The source travels with them: once saved, the row lives in
        // whichever table this level owns.
        newEntries
            .filter(e => !e.error)
            .forEach(e => onAddSessionUpload && onAddSessionUpload({ id: e.id, file: e.file, preview: e.preview, name: e.name, source: ownSource }));
        // Auto-select the last successfully-uploaded file so "Confirm" actually uses it
        const lastValid = [...newEntries].reverse().find(e => !e.error);
        if (lastValid) {
            setSelected({ id: lastValid.id, url: lastValid.preview, file: lastValid.file, name: lastValid.name, isUploaded: true, source: ownSource });
        }
    };

    const handleFileInputChange = (e) => {
        handleFilesSelected(e.target.files);
        e.target.value = '';
    };

    const removeUploaded = (id) => {
        const removed = uploadedImages.find(i => i.id === id);
        // The pinned "current" tile (id: '__current__') isn't a real backend row — it must
        // never be sent to the backend as something to delete.
        if (removed?.isSaved && removed.id != null && !removed.isCurrentPin) {
            // url and name ride along so the steps can re-point rows that use this picture
            // under a DIFFERENT row id — the listing and an item each keep their own copy,
            // so the id alone only ever matches the level the tile came from.
            const entry = {
                id: removed.id,
                source: removed.source || ownSource,
                url: removed.preview || null,
                name: removed.name || null,
            };
            setRemovedSaved(prev => (
                prev.some(r => r.id === entry.id && r.source === entry.source) ? prev : [...prev, entry]
            ));
        }
        // A session upload (not yet saved) lives in the shared pool — drop it there too so it
        // disappears from the other level's Upload tab as well (ERP-5022).
        if (removed?.file && !removed.isSaved && !removed.error && !removed.isCurrentPin) {
            onRemoveSessionUpload && onRemoveSessionUpload(id);
        }
        const remaining = uploadedImages.filter(i => i.id !== id && !i.error);
        setUploadedImages(prev => prev.filter(i => i.id !== id));
        setSelected(prev => {
            if (!prev) return prev;
            // A saved upload loaded from the backend as the active image carries id: null and
            // the row's own copied file url. That copy is written under a different name than
            // the history row it came from (both land in images/tradein/, so the second save
            // gets suffixed), so neither id nor url can match it back to its tile — there is no
            // link in the GET response between tradein_image_url and any tradein_images entry.
            // Treat that state as a hit: leaving it pointed at an image the user just deleted
            // is what made the removal look like it was ignored.
            const ambiguous = Boolean(removed?.isSaved) && isPoolTab(prev.tab) && prev.id == null;
            const isActive = prev.id === id || (removed?.preview && prev.url === removed.preview) || ambiguous;
            if (!isActive) return prev;
            // Prefer another uploaded image, so a removal stays within the user's own pictures;
            // fall back to the SPU's first, then to the device default (always present and
            // non-deletable) so the selection never ends up empty.
            const next = remaining[0];
            if (next) {
                return next.isSaved
                    ? selectionFromSaved(next)
                    : { id: next.id, url: next.preview, file: next.file, name: next.name, isUploaded: true, source: next.source || ownSource };
            }
            if (images[0]) return { ...images[0], tab: 'spu' };
            return deviceDefaultImage ? { id: null, url: deviceDefaultImage, isPlaceholder: true, isDeviceDefault: true } : null;
        });
    };

    const tabStyle = (tab) => ({
        height: '35px',
        minWidth: 88,
        padding: '10px',
        fontSize: 12,
        fontWeight: 600,
        fontFamily: 'Montserrat',
        cursor: 'pointer',
        border: 'none',
        borderRadius: 8,
        background: activeTab === tab ? YELLOW : 'transparent',
        color: activeTab === tab ? '#212529' : '#6C757D',
    });

    return (
        <>
            <div
                className="drawer-overlay"
                onClick={onCancel}
                style={{ zIndex: 1040 }}
            />
            <div style={{
                position: 'fixed',
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                background: '#fff',
                borderRadius: 8,
                boxShadow: '0px 4px 24px rgba(0,0,0,0.18)',
                zIndex: 1050,
                width: '90%',
                maxWidth: 1130,
                minHeight: '640px',
                display: 'flex',
                flexDirection: 'column',
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '15px 20px',
                    // borderBottom: '1px solid #E9ECEF',
                    borderRadius: '12px 12px 0 0',
                    background: '#F8F9FA',
                    flexShrink: 0,
                }}>
                    <span style={{ fontWeight: 600, fontSize: 14, fontFamily: 'Montserrat', color: '#212529' }}>
                        Select or Upload {imageLabel}<span style={{ color: '#DC3545' }}>*</span>
                    </span>
                    <button
                        type="button"
                        onClick={onCancel}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}
                    >
                        <X size={18} color="#000" />
                    </button>
                </div>


                {/* Body */}
                <div style={{ padding: '20px', flex: 1, overflowY: 'auto' }}>
                    {/* Tabs */}
                    <div style={{
                        display: 'flex', flexShrink: 0, borderRadius: '8px', padding: '10px', marginBottom: 16,
                        background: '#FFF', boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)', gap: 8,
                    }}>
                        <button type="button" style={tabStyle('upload')} onClick={() => setActiveTab('upload')}>Upload</button>
                        <button type="button" style={tabStyle('spu')} onClick={() => setActiveTab('spu')}>SPU</button>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                        <span style={{ fontSize: 12, color: '#6C757D', fontFamily: 'Montserrat' }}>
                            {activeTab === 'spu'
                                ? `Showing all images of the referenced SPU in ERP. Select one to use as the ${imageLabel}.`
                                : 'Only PNG/WEBP/JPEG files below 1MB are support. Duplicate filenames cannot be uploaded, please rename files locally before uploading.'}
                        </span>
                        <span style={{ fontSize: 12, color: '#6C757D', fontFamily: 'Montserrat' }}>
                            Total: {activeTab === 'spu' ? displaySpuImages.length : uploadedImages.length + (deviceDefaultImage ? 1 : 0)}
                        </span>
                    </div>

                    {activeTab === 'spu' ? (
                        displaySpuImages.length === 0 ? (
                            <div style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                textAlign: 'center', padding: '80px 0', gap: 12,
                            }}>
                                <img src={noBrandImage} alt="No SPU" style={{ width: 96, height: 96, objectFit: 'contain' }} />
                                <span style={{ fontSize: 20, fontWeight: 600, fontFamily: 'Montserrat', color: '#212529' }}>
                                    No SPU Image Exists
                                </span>
                                <span style={{ fontSize: 14, fontFamily: 'Montserrat', color: '#6C757D' }}>
                                    Nothing here yet. Upload image for Trade-In.
                                </span>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, padding: '10px 0px 20px 0px' }}>
                                {displaySpuImages.map(img => (
                                    <div
                                        key={img.id}
                                        onClick={() => toggle(img)}
                                        style={{
                                            border: `1px solid ${isSelected(img) ? YELLOW : '#CED4DA'}`,
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                            aspectRatio: '1 / 1',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: '#FFFFFF',
                                            boxSizing: 'border-box',
                                            transition: 'border-color 0.15s',
                                            width: '115px',
                                            height: '118px',
                                            padding: '10px',
                                        }}
                                    >
                                        <img src={img.url || noImage} alt={img.isCurrentPin ? 'Currently selected image' : `img ${img.id}`} onError={e => { e.target.onerror = null; e.target.src = noImage; }} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                    </div>
                                ))}
                            </div>
                        )
                    ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, padding: '10px 0px 20px 0px' }}>
                            {/* Hidden file input — multiple files, filtered to accepted image types */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/webp,image/jpeg"
                                multiple
                                onChange={handleFileInputChange}
                                style={{ display: 'none' }}
                            />

                            {/* Upload tile */}
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                style={{
                                    border: '1px dashed #CED4DA',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 6,
                                    width: '115px',
                                    height: '118px',
                                    color: '#6C757D',
                                    fontSize: 12,
                                    fontFamily: 'Montserrat',
                                }}
                            >
                                <Plus size={20} />
                                Upload
                            </div>

                            {/* Device default image (ERP-5022): brand placeholder for the Step-1
                                Listing Device, selected by default and non-deletable (no remove badge). */}
                            {deviceDefaultImage && (
                                <UploadTileTooltip label="Default image">
                                    <div
                                        onClick={() => setSelected({ id: null, url: deviceDefaultImage, isPlaceholder: true, isDeviceDefault: true })}
                                        style={{
                                            position: 'relative',
                                            border: `1px solid ${selected?.isDeviceDefault ? YELLOW : '#CED4DA'}`,
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: '#FFFFFF',
                                            boxSizing: 'border-box',
                                            transition: 'border-color 0.15s',
                                            width: '115px',
                                            height: '118px',
                                            padding: '10px',
                                        }}
                                    >
                                        <img src={deviceDefaultImage} alt="Device default" onError={e => { e.target.onerror = null; e.target.src = noImage; }} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                    </div>
                                </UploadTileTooltip>
                            )}

                            {uploadedImages.map(img => {
                                const uploadedSelected = selected?.id === img.id;
                                return img.error ? (
                                    <UploadTileTooltip key={img.id} label={img.error}>
                                        <div
                                            style={{
                                                position: 'relative',
                                                border: '1px solid #DC3545',
                                                borderRadius: 4,
                                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                                                background: '#F8F9FA',
                                                boxSizing: 'border-box',
                                                width: '115px',
                                                height: '118px',
                                            }}
                                        >
                                            <XCircle size={22} color="#DC3545" />
                                            <span style={{ color: '#DC3545', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 600 }}>Error</span>
                                            <RemoveImageControl onRemove={() => removeUploaded(img.id)} />
                                        </div>
                                    </UploadTileTooltip>
                                ) : (
                                    <UploadTileTooltip key={img.id} label={img.name}>
                                        <div
                                            onClick={() => toggleUploaded(img)}
                                            style={{
                                                position: 'relative',
                                                border: `1px solid ${uploadedSelected ? YELLOW : '#CED4DA'}`,
                                                borderRadius: 4,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: '#FFFFFF',
                                                boxSizing: 'border-box',
                                                transition: 'border-color 0.15s',
                                                width: '115px',
                                                height: '118px',
                                                padding: '10px',
                                            }}
                                        >
                                            <img src={img.preview} alt={img.isCurrentPin ? 'Currently selected image' : img.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                            {/* The pinned "current" tile isn't a real history row (see uploadCurrentTile) — nothing to remove */}
                                            {!img.isCurrentPin && <RemoveImageControl onRemove={() => removeUploaded(img.id)} />}
                                        </div>
                                    </UploadTileTooltip>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 12,
                    padding: '20px 0px',
                    margin: '0px 20px',
                    borderTop: '1px solid #E9ECEF',
                    flexShrink: 0,
                }}>
                    <button type="button" className="dark-button" onClick={onCancel}
                        style={{
                            fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12,
                            borderRadius: '8px',
                            border: '1px solid #212529',
                            background: '#FFF',
                            height: '34px',
                            minWidth: '100px',
                            padding: '10px',
                            boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                        Cancel
                    </button>
                    <button type="button" className="dark-border"
                        disabled={!selected}
                        onClick={() => onConfirm(
                            selected?.isCurrentPin ? initialSelected : selected,
                            // Persist this level's own already-saved history and its own fresh
                            // uploads, plus whichever pool entry (from the other level) is
                            // actively selected — but not every other pool entry passing through.
                            uploadedImages.filter(i => !i.error && i.id !== '__current__' &&
                                (ownIdsRef.current.has(i.id) || i.id === selected?.id)),
                            removedSaved,
                        )}
                        style={{
                            fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12,
                            borderRadius: '8px',
                            border: `1px solid ${selected ? '#212529' : '#CED4DA'}`,
                            background: selected ? '#212529' : '#E9ECEF',
                            height: '34px',
                            minWidth: '100px',
                            padding: '10px',
                            color: selected ? 'white' : '#6C757D',
                            boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: selected ? 'pointer' : 'not-allowed',
                        }}>
                        Confirm
                    </button>
                </div>
            </div>
        </>
    );
}
